/**
 * promoInstall.ts — List & Earn: swaps app.js's demo PROMO fixture for the
 * real thing, and makes the opt-in button actually enroll.
 *
 * Asana 1218034213981009. Server half is api-server routes/listAndEarn.ts
 * (slice 2, on develop); the ladder and enrollment schema are slice 1.
 *
 * ── THE GATE IS THE POINT ───────────────────────────────────────────────────
 * All four promo surfaces — the profile chip, the two wallet rows, and the
 * #/promotion page — render only when PROMO.source === "api" (app.js
 * promoInstalled()). Nothing here sets that flag directly: the bridge sets it
 * LAST, after every field has landed. Absent, 500, unparseable, or a payload
 * we cannot map all leave the flag alone, so the surfaces stay dark.
 *
 * That is deliberate and it matches the server, whose 500 returns no body
 * shape "because a half-filled PromoStatus would let the floor paint a
 * promotion with holes in it". No promo is a correct state. A promo with holes
 * is a money claim we cannot honour.
 *
 * ── THREE TRANSLATIONS, AND WHY EACH IS NOT OPTIONAL ────────────────────────
 * 1. MONEY. The wire is cents (listAndEarn.ts: credit_cents,
 *    locked_credit_cents — correct; money should be integers). Every render
 *    site prints `$${...}` raw. Ship the payload unconverted and the offer
 *    reads "$2500 off your fees" — 100x, on a money surface, and plausible
 *    enough to survive review because it looks generous rather than broken.
 *
 * 2. ABSENCE. The server sends null wherever a number does not apply — tier
 *    unless "live", lockedCredit unless locked, the day counts outside their
 *    states. app.js's dayLabel() is `n + " days"` with no guard, so a null
 *    renders the literal "null days", and `$${PROMO.lockedCredit}` renders
 *    "$null". Every field is defaulted here, at the boundary, so no render
 *    site has to know.
 *
 * 3. FIELDS THE SERVER DOES NOT SEND. PromoStatus carries no tierIndex and no
 *    source, but promoTier() is `PROMO_LADDER[Math.min(PROMO.tierIndex, …)]`.
 *    Spread the payload naively and that is Math.min(undefined, 3) → NaN →
 *    PROMO_LADDER[NaN] → undefined, and renderPromotion throws on its fourth
 *    line. The payload does not merely misprint without this — it crashes.
 */

import { EMPTY_DATE, formatDate, parseServerDate } from "../lib/datetime";

export const OPT_IN_FAILED_MESSAGE = "Could not start the promotion — try again";
export const OPT_IN_CLOSED_MESSAGE = "This promotion is no longer available";

/** Mirrors api-server PromoStatus. Every nullable field is nullable here too. */
export interface PromoStatusWire {
  state: "offered" | "live" | "locked" | "expired" | "none";
  tier: { credit: number; items: number; days: number } | null;
  listed: number;
  daysLeftInWindow: number | null;
  daysLeftInPromo: number | null;
  lockedCredit: number | null;
  eligibleDaysLeft: number | null;
  ladder: Array<{ credit: number; items: number; days: number }>;
  /**
   * When the List & Earn credit was issued and when it expires, ISO strings
   * read off the credit row itself (#1041, 2026-10-06). Null until the credit
   * exists, including a lock still waiting on payment. Optional because a
   * server from before #1041 omits them, and an absent date reads as a null.
   */
  issuedAt?: string | null;
  expiresAt?: string | null;
}

/** The shape app.js's PROMO fixture uses. Dollars, never null. */
export interface PromoView {
  state: PromoStatusWire["state"];
  tierIndex: number;
  listed: number;
  daysLeftInWindow: number;
  /**
   * 🪤 NULLABLE ON PURPOSE, against this file's own "never null" rule.
   * The server sends this ONLY while state is "live" (lib/listAndEarn.ts), yet
   * app.js's promotion page renders its header count ("N days left in the
   * promotion") on the LOCKED payout screen too. Defaulting it to 0 once told a
   * locked user their credit would arrive "in 0 days" — a false statement about
   * when money arrives, on the screen whose entire job is saying when money
   * arrives. Since Tyler's ruling A (2026-09-29, card 1218020300301052) the
   * credit is added the day the window fills, so the locked screen's payout
   * sentence names no close date at all (copy ratified by Blake 2026-10-05,
   * card 1219177863701651); the header count still reads this field. 0 cannot
   * mean "unknown" because 0 is a real answer for a live promo closing today,
   * so absence has to stay absence and the render site omits the count.
   * Raised in adversarial review.
   */
  daysLeftInPromo: number | null;
  lockedCredit: number;
  eligibleDaysLeft: number;
  /**
   * The credit's issue and expiry DATES ("Oct 12, 2026"), formatted by the
   * same formatDate the wallet uses for its own dates (floorTransport.ts
   * mapWallet), or "" when the status carries none. "" and never null, by this
   * file's rule: app.js paints both, and branches on `issued` to say the
   * credit "was added" or "is on its way" (2026-10-06, card 1219177863701651).
   */
  issued: string;
  expires: string;
  /**
   * Whether that credit has already expired: `expiresAt` at or before the
   * clock mapPromo was given. The one computed thing on this view, and only a
   * comparison, never a date. The server never leaves "locked" and keeps
   * sending both dates after the credit expires (lib/listAndEarn.ts reads
   * the credit row, spent or not), so app.js ends the enrollment's surfaces
   * on this flag: the chip, the wallet row and the page go away and
   * #/promotion falls back to the wallet (promoStillEarned; Blake,
   * 2026-10-07, "disappear", after the adversarial review of 2026-10-06
   * found day 61 reading "it expires {a past date}"). false while nothing is
   * issued or the expiry does not parse, so a credit with no readable expiry
   * stays in view rather than vanishing on a parse failure.
   */
  creditExpired: boolean;
}

export interface LadderRungView {
  credit: number;
  items: number;
  days: number;
}

interface PromoWindow extends Window {
  __gbzInstallPromo?: (promo: PromoView, ladder: LadderRungView[]) => void;
  __gbzPromoOptIn?: () => void;
  /** Blake's toast (app.js). Absent in a bare fixture; never assumed. */
  toast?: (msg: string) => void;
}

/** Cents on the wire, dollars on the screen. The one conversion that matters. */
export function dollars(cents: number | null | undefined): number {
  if (typeof cents !== "number" || !Number.isFinite(cents)) return 0;
  return cents / 100;
}

/** Null is a real answer from this server; it is never a number on screen. */
function num(v: number | null | undefined): number {
  return typeof v === "number" && Number.isFinite(v) ? v : 0;
}

/** A status timestamp as the wallet prints its dates, or "". Never
 *  formatDate's EMPTY_DATE placeholder, which would paint "added —". Only a
 *  string is a date here: the server sends a string or null, and an object in
 *  that slot would throw inside the formatter's .trim(), on a payload the gate
 *  has already let through. */
function dateOrBlank(v: string | null | undefined): string {
  if (typeof v !== "string" || !v) return "";
  const text = formatDate(v);
  return text === EMPTY_DATE ? "" : text;
}

/** Whether a status timestamp is at or before `now`. Parsed by the rule
 *  formatDate parses it (parseServerDate: a zone-less string reads as UTC),
 *  and a string that will not parse is no expiry, so it is never "expired":
 *  the view paints "" for it and says nothing about a date. Inclusive at the
 *  instant, as the ledger counts a credit spendable only while
 *  `expires_at > now()` (api-server lib/creditLedger.ts). */
function isPast(v: string | null | undefined, now: number): boolean {
  if (typeof v !== "string" || !v) return false;
  const at = parseServerDate(v);
  return at !== null && at.getTime() <= now;
}

function say(win: PromoWindow, message: string): void {
  if (typeof win.toast === "function") win.toast(message);
}

/**
 * Which rung the UI should point at.
 *
 * 🪤 The locked case is not the open rung. renderPromotion reads the WON rung
 * as `PROMO.tierIndex - 1` (one payout, Blake ruling 4: filling a window ends
 * the ladder; since Tyler's ruling A, 2026-09-29, card 1218020300301052, the
 * credit is also issued that same day), so a locked enrollment has to sit one
 * PAST the rung it won or the page credits the wrong amount. The won rung is
 * found by its credit, because lockedCredit is the only thing the server
 * sends about it.
 */
export function tierIndexFor(status: PromoStatusWire, ladder: LadderRungView[]): number {
  if (status.state === "locked") {
    const won = ladder.findIndex((r) => r.credit === dollars(status.lockedCredit));
    /* 🪤 NO MATCH MEANS NO RUNG, AND THAT IS NOT ladder.length.
       An earlier draft returned ladder.length here and called it "pointing past
       the end". It is not: renderPromotion reads the won rung as tierIndex - 1,
       so ladder.length marks the BOTTOM rung "Earned" and reports ITS item
       count as what the user listed — the wrong amount against the wrong rung,
       on the payout screen. 0 sends wonIndex to -1, which marks nothing, and
       app.js's own `PROMO_LADDER[tierIndex - 1] ? … : PROMO.listed` fallback
       then reports what the server actually said. Raised in review. */
    return won >= 0 ? won + 1 : 0;
  }
  if (status.state === "live" && status.tier) {
    const openCredit = dollars(status.tier.credit);
    const at = ladder.findIndex((r) => r.credit === openCredit);
    if (at >= 0) return at;
  }
  /* offered / expired / none, and a "live" whose rung did not resolve: the top
     rung is what the offer quotes, and it is the only index that is always in
     range. */
  return 0;
}

/** The whole translation, in one pure function so the tests can hold it still;
 *  `now` is the clock creditExpired is read against, injectable for the same
 *  reason, and the wall clock when nothing is passed. */
export function mapPromo(status: PromoStatusWire, now: number = Date.now()): { promo: PromoView; ladder: LadderRungView[] } {
  const ladder: LadderRungView[] = (Array.isArray(status.ladder) ? status.ladder : []).map((r) => ({
    credit: dollars(r?.credit),
    items: num(r?.items),
    days: num(r?.days),
  }));

  return {
    ladder,
    promo: {
      state: status.state,
      tierIndex: tierIndexFor(status, ladder),
      listed: num(status.listed),
      daysLeftInWindow: num(status.daysLeftInWindow),
      daysLeftInPromo: typeof status.daysLeftInPromo === "number" && Number.isFinite(status.daysLeftInPromo)
        ? status.daysLeftInPromo
        : null,
      lockedCredit: dollars(status.lockedCredit),
      eligibleDaysLeft: num(status.eligibleDaysLeft),
      issued: dateOrBlank(status.issuedAt),
      expires: dateOrBlank(status.expiresAt),
      creditExpired: isPast(status.expiresAt, now),
    },
  };
}

/**
 * The smallest ladder app.js's markup can actually express.
 *
 * 🪤 NOT A STYLE RULE — A CRASH. app.js:3108 renders the offer's second line,
 * "Miss it and the next window opens automatically, down to $X for posting Y
 * listings", off `PROMO_LADDER[1]` with NO guard. That index was safe for as
 * long as PROMO_LADDER was a hardcoded four-rung constant. It stopped being
 * safe the moment this module started replacing that array from the server,
 * and the server builds it from `promo_tiers` ROWS (api-server
 * lib/listAndEarn.ts) — so the length is a DATA state: a partial seed, a
 * deleted row, a re-cut campaign. One rung and renderWallet throws mid-template,
 * which does not show an error, it shows a wallet that never paints.
 *
 * Refusing is also the honest answer, not merely the safe one: with one rung
 * there IS no next window, so that sentence has nothing true to say.
 */
const MIN_LADDER_RUNGS = 2;

/** A payload we cannot trust is the same as no payload: leave the gate shut. */
function usable(body: unknown): body is PromoStatusWire {
  if (!body || typeof body !== "object") return false;
  const b = body as PromoStatusWire;
  const s = b.state;
  const known = s === "offered" || s === "live" || s === "locked" || s === "expired" || s === "none";
  /* An absent or too-short ladder is NOT survivable by falling back to the
     seeded rungs: that would quote $25/$20/$15/$10 the server never sent, on a
     money surface, which is the exact fabrication this whole module exists to
     end. Dark beats invented. */
  return known && Array.isArray(b.ladder) && b.ladder.length >= MIN_LADDER_RUNGS;
}

export async function installPromo(
  win: PromoWindow,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  /** The clock the credit's expiry is read against; the tests hold it still. */
  clock: () => number = Date.now,
): Promise<void> {
  const bridge = win.__gbzInstallPromo;
  if (typeof bridge !== "function") return; /* bridge script has not run — nothing to install into */

  const apply = (body: unknown): boolean => {
    if (!usable(body)) return false;
    const { promo, ladder } = mapPromo(body, clock());
    bridge(promo, ladder);
    return true;
  };

  const read = async (): Promise<void> => {
    let res: Response;
    try {
      res = await authFetch("/api/promo/list-and-earn");
    } catch {
      return; /* offline: the surfaces stay dark, which is the honest state */
    }
    if (!res.ok) return;
    try {
      apply(await res.json());
    } catch {
      /* a 200 whose body will not parse is not a promotion — same rule as a 500 */
    }
  };

  /* THE OPT-IN. app.js's own handler defers to this hook when it exists, so
     the fabricating demo branch cannot run on the live install. Nothing moves
     until the server agrees — the button's claim is about money. */
  /* 🪤 ONE POST PER INTENT. The server survives a double click — it answers
     the replay "already_enrolled" and says so explicitly ("Lost a race with the
     user's own second click") — but surviving is not the same as being right
     here: two clicks meant two toasts, and on a slow connection an impatient
     second click on a MONEY control while the first is still open is the
     normal case, not the edge one. Raised in review as untested; it was also
     unhandled. */
  let optInFlight = false;

  win.__gbzPromoOptIn = () => {
    if (optInFlight) return;
    optInFlight = true;
    void (async () => {
      let res: Response;
      try {
        res = await authFetch("/api/promo/list-and-earn/opt-in", { method: "POST" });
      } catch {
        optInFlight = false;
        say(win, OPT_IN_FAILED_MESSAGE);
        return;
      }

      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        body = null;
      }

      if (res.ok) {
        /* The POST answers with the same shape, post-enrollment. If it is
           unusable we do NOT fabricate a live state — re-read instead, and if
           that fails too the surfaces simply do not move. */
        if (!apply(body)) await read();
        optInFlight = false;
        return;
      }

      /* 409 is the server declining: inactive campaign, cap full, too late.
         It is a real answer, not an error — say so plainly and re-read, so the
         surfaces settle on what is actually true now. */
      say(win, res.status === 409 ? OPT_IN_CLOSED_MESSAGE : OPT_IN_FAILED_MESSAGE);
      await read();
      optInFlight = false;
    })();
  };

  await read();
}
